import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Camera } from 'lucide-react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';

const CheckInModal = ({ isOpen, onClose, rental, truck, onSuccess, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    km_inicial: '',
    combustivel_inicial: ''
  });
  const [fotos, setFotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef();
  const sigCanvas = useRef();

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const uploadedIds = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/api/trucks/upload-image`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedIds.push(response.data.file_id);
      }

      setFotos([...fotos, ...uploadedIds]);
      toast.success(language === 'pt-BR' ? `✅ ${files.length} foto(s) adicionada(s)` : `✅ ${files.length} photo(s) added`);
    } catch (error) {
      console.error('Erro upload:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao fazer upload' : 'Upload error');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFotos(fotos.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.km_inicial || !formData.combustivel_inicial) {
      toast.error(language === 'pt-BR' ? 'Preencha KM e combustível' : 'Fill in KM and fuel');
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      toast.error(language === 'pt-BR' ? 'Assine o termo de responsabilidade' : 'Sign the liability term');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const assinatura = sigCanvas.current.toDataURL();

      await axios.put(`${API_URL}/api/rentals/${rental.id}/checkin`, {
        km_inicial: parseFloat(formData.km_inicial),
        combustivel_inicial: formData.combustivel_inicial,
        fotos: fotos,
        assinatura: assinatura
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'pt-BR' ? '✅ Check-in realizado!' : '✅ Check-in completed!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro no check-in' : 'Check-in error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🚛 Check-in - {truck?.modelo} ({truck?.placa})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
            <p className="text-sm font-semibold text-yellow-800">{language === 'pt-BR' ? '📋 Termo de Responsabilidade' : '📋 Liability Term'}</p>
            <p className="text-xs text-yellow-700 mt-2">
              {language === 'pt-BR' ? 'Declaro estar recebendo este veículo e me comprometo a devolvê-lo nas mesmas condições, pagar $100/dia, comunicar problemas imediatamente e assumir responsabilidade por danos.' : 'I declare I am receiving this vehicle and commit to returning it in the same condition, pay $100/day, report issues immediately and assume responsibility for damages.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Quilometragem Inicial (km)' : 'Initial Mileage (km)'} *</Label>
              <Input 
                type="number"
                value={formData.km_inicial}
                onChange={(e) => setFormData({...formData, km_inicial: e.target.value})}
                placeholder={truck?.quilometragem_atual?.toString()}
              />
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Nível Combustível' : 'Fuel Level'} *</Label>
              <Select value={formData.combustivel_inicial} onValueChange={(v) => setFormData({...formData, combustivel_inicial: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'pt-BR' ? 'Selecione' : 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vazio">⛽ {language === 'pt-BR' ? 'Vazio' : 'Empty'}</SelectItem>
                  <SelectItem value="1/4">⛽ 1/4</SelectItem>
                  <SelectItem value="1/2">⛽ 1/2</SelectItem>
                  <SelectItem value="3/4">⛽ 3/4</SelectItem>
                  <SelectItem value="Cheio">⛽ {language === 'pt-BR' ? 'Cheio' : 'Full'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Fotos do Caminhão' : 'Truck Photos'}</Label>
            <div className="space-y-3">
              {/* Upload Area */}
              <div 
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 font-semibold">
                  {language === 'pt-BR' ? 'Clique para adicionar fotos' : 'Click to add photos'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt-BR' ? 'Tire fotos do estado atual do caminhão' : 'Take photos of current truck condition'}
                </p>
                {uploadingPhoto && (
                  <p className="text-xs text-blue-600 mt-2">{language === 'pt-BR' ? 'Enviando...' : 'Uploading...'}</p>
                )}
              </div>
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />

              {/* Photos Preview */}
              {fotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {fotos.map((fileId, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">
                {fotos.length} {language === 'pt-BR' ? 'foto(s) adicionada(s)' : 'photo(s) added'}
              </p>
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Assinatura Digital' : 'Digital Signature'} *</Label>
            <div className="border-2 rounded-lg bg-white">
              <SignatureCanvas 
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-40 cursor-crosshair'
                }}
              />
            </div>
            <Button 
              type="button" 
              size="sm" 
              variant="outline" 
              onClick={() => sigCanvas.current?.clear()}
              className="mt-2"
            >
              {language === 'pt-BR' ? '🗑️ Limpar' : '🗑️ Clear'}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? (language === 'pt-BR' ? 'Processando...' : 'Processing...') : (language === 'pt-BR' ? '✅ Confirmar Check-in' : '✅ Confirm Check-in')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckInModal;